import React, { useEffect, useRef, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import {
  fetchBlogsList,
  deleteBlogById,
  toggleBlogStatus,
} from "../../services/axios/apiManager";
import BSPagination from "../../Components/pagination";
import LoadingSpinner from "../../Components/spinner";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import $ from "jquery";
import "datatables.net-bs5";

const BlogsList = ({ granted }) => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalPages: 0,
    totalRecords: 0,
  });
  const [loading, setLoading] = useState(false);
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [modalOptions, setModalOptions] = useState({});
  const tableRef = useRef(null);

  const fetchBlogs = async (page = 1) => {
    setLoading(true);
    try {
      const response = await fetchBlogsList(page, pagination.pageSize || 10);
      if (response?.data?.success) {
        setBlogs(response.data.data);
        setPagination((prev) => ({
          ...prev,
          ...response.data.pagination,
          pageSize: response.data.pagination.pageSize || prev.pageSize,
        }));
      }
    } catch (error) {
      console.error("Error fetching blogs:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  const handlePageChange = (page) => {
    fetchBlogs(page);
  };

  const handleOpenAddEditPage = (blog = {}) => {
    if (blog?.id) {
      navigate(`/BlogsManagement/edit/${blog.id}`);
    } else {
      navigate("/BlogsManagement/add");
    }
  };

  const handleToggleStatus = (blog) => {
    setModalOptions({
      header: "Change Status",
      body: `Are you sure you want to ${blog.status === 1 ? "deactivate" : "activate"} "${blog.title}"?`,
      okay: "Confirm",
      method: async () => {
        const response = await toggleBlogStatus({
          id: blog.id,
          status: blog.status === 1 ? 0 : 1,
        });
        if (response?.data?.success) {
          toast.success("Status updated");
          fetchBlogs(pagination.currentPage);
        }
        setShowOptionsModal(false);
      },
    });
    setShowOptionsModal(true);
  };

  const handleDelete = (blog) => {
    setModalOptions({
      header: "Delete Blog",
      body: `Are you sure you want to delete "${blog.title}"?`,
      okay: "Delete",
      method: async () => {
        const response = await deleteBlogById(blog.id);
        if (response?.data?.success) {
          toast.success("Blog deleted");
          fetchBlogs(pagination.currentPage);
        }
        setShowOptionsModal(false);
      },
    });
    setShowOptionsModal(true);
  };

  useEffect(() => {
    const rows = blogs.map((b) => [
      b.title,
      b.employee_author
        ? `${b.employee_author.first_name} ${b.employee_author.last_name}`
        : b.user_author
          ? `${b.user_author.first_name} ${b.user_author.last_name}`
          : "N/A",
      b.author_type,
      b.comments_to_review,
      b.commentable === 1 ? "Yes" : "No",
      b.createdAt ? new Date(b.createdAt).toLocaleDateString() : "N/A",
      b.status === 1 ? "Published" : "Draft",
      "", // Actions
    ]);

    const config = {
      data: rows,
      columns: [
        { title: "Title" },
        { title: "Author" },
        { title: "Author Type" },
        { title: "Comments" },
        { title: "Allow Comments" },
        { title: "Date" },
        { title: "Status" },
        { title: "Actions", orderable: false },
      ],
      destroy: true,
      paging: false,
      searching: false,
      info: false,
      responsive: true,
      createdRow: (row, data, idx) => {
        const blog = blogs[idx];
        const $row = $("td", row);

        // Comments to review
        const $toReview = $(
          `<button class="btn btn-link p-0 text-decoration-none text-nowrap ${blog.comments_to_review > 0 ? "text-warning" : "text-success"
          }">
          ${blog.total_comments} comments${blog.comments_to_review > 0
            ? ` - ${blog.comments_to_review} to review`
            : ""
          }
          <i class="fas fa-external-link-alt ms-1" style="font-size: 0.8rem;"></i>
        </button>`,
        ).on("click", () => navigate(`/BlogsManagement/comments/${blog.id}`));
        $row.eq(3).empty().append($toReview);

        // Allow Comments status color
        $row
          .eq(4)
          .addClass(blog.commentable === 1 ? "text-success" : "text-danger");

        // Status toggle
        const $status = $(
          `<button class="btn btn-link p-0 ${blog.status === 1 ? "text-success" : "text-warning"}">${data[6]}</button>`,
        ).on("click", () => handleToggleStatus(blog));
        $row.eq(6).empty().append($status);

        // Actions (eq(7) since index 6 is Status)
        const $act = $row.eq(7).empty().css("display", "flex");
        $act.append(
          $(
            `<button class="btn btn-outline-primary btn-sm me-1"><i class="fa fa-edit"></i></button>`,
          ).on("click", () => handleOpenAddEditPage(blog)),
        );

        !blog?.status &&
          $act.append(
            $(
              `<button class="btn btn-outline-danger btn-sm"><i class="fa fa-trash"></i></button>`,
            ).on("click", () => handleDelete(blog)),
          );
      },
    };

    const $tbl = $(tableRef.current);
    if ($.fn.dataTable.isDataTable($tbl)) {
      $tbl.DataTable().clear().rows.add(rows).draw();
    } else {
      $tbl.DataTable(config);
    }
    return () => {
      if ($.fn.dataTable.isDataTable($tbl)) $tbl.DataTable().destroy();
    };
  }, [blogs]);

  return (
    <div className="container text-light bg-dark rounded mb-4 p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3>Blog Posts</h3>
        <div>
          <Button
            variant="outline-success"
            className="me-2"
            onClick={() => navigate("/BlogsManagement/categories")}
          >
            Blog Categories
          </Button>
          <Button variant="success" onClick={() => handleOpenAddEditPage({})}>
            Add New Blog
          </Button>
        </div>
      </div>

      <div className="position-relative">
        {loading && (
          <div
            className="position-absolute top-50 start-50 translate-middle"
            style={{ zIndex: 10 }}
          >
            <LoadingSpinner size="lg" />
          </div>
        )}

        <div className={`table-responsive ${loading ? "opacity-50" : ""}`}>
          <table
            ref={tableRef}
            className="table table-dark table-striped w-100"
          />

          <div className="d-flex justify-content-end mt-4">
            <BSPagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </div>

      <Modal
        show={showOptionsModal}
        onHide={() => setShowOptionsModal(false)}
        centered
      >
        <Modal.Header closeButton className="bg-dark text-light border-0">
          <Modal.Title>{modalOptions.header}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-dark text-light">
          {modalOptions.body}
        </Modal.Body>
        <Modal.Footer className="bg-dark text-light border-0">
          <Button
            variant="secondary"
            onClick={() => setShowOptionsModal(false)}
          >
            Cancel
          </Button>
          <Button variant="danger" onClick={modalOptions.method}>
            {modalOptions.okay}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default BlogsList;
